import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/** Convert technical DB errors into user-friendly messages */
function getFriendlyErrorMessage(error: unknown, fallback: string, metricId?: string): string {
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error !== null && "message" in error && typeof (error as { message?: unknown }).message === "string"
        ? (error as { message: string }).message
        : String(error);
  if (
    msg.includes("foreign key") ||
    msg.includes("os_incoming_event_transformation_log") ||
    msg.includes("incoming_metric")
  ) {
    const base = "A log in the database references this metric. It may not appear in the list (only the 200 most recent logs are shown).";
    const searchHint = metricId
      ? ` Go to Incoming Event Log and search for metric ID "${metricId}" to find it.`
      : "";
    const migrationHint = " Run the migration (20260204150000) to allow deletion without removing the log.";
    return base + searchHint + migrationHint;
  }
  return msg || fallback;
}

/** Max pageSize to prevent OOM. Use pagination for larger datasets. */
const MAX_PAGE_SIZE = 5000;

export interface BusinessMetricsFilters {
  dateFrom?: string;
  dateTo?: string;
}

/** Single brand or multiple brands (for holdco aggregated view). Multi-brand: fetches all rows for brand_id IN (ids); aggregation is SUM (add quantities). */
export const useBusinessMetrics = (brandId: string | string[], page = 0, pageSize = 100, filters?: BusinessMetricsFilters) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const brandIds = Array.isArray(brandId) ? brandId : [brandId];
  const hasBrands = brandIds.length > 0;

  // Cap pageSize to prevent memory issues
  const effectivePageSize = Math.min(pageSize, MAX_PAGE_SIZE);

  const fetchMetrics = async () => {
    const SUPABASE_MAX = 1000;

    // If requested size fits in one call, use simple range
    // Helper to apply shared filters (brand + date) to a query builder
    const applyFilters = (q: any) => {
      if (brandIds.length === 1) {
        q = q.eq('brand_id', brandIds[0]);
      } else {
        q = q.in('brand_id', brandIds);
      }
      if (filters?.dateFrom) q = q.gte('date', filters.dateFrom);
      if (filters?.dateTo) q = q.lte('date', filters.dateTo);
      return q;
    };

    if (effectivePageSize <= SUPABASE_MAX) {
      const start = page * effectivePageSize;
      const end = start + effectivePageSize - 1;
      let q = supabase
        .from('incoming_business_metrics')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(start, end);
      q = applyFilters(q);
      const { data, error, count } = await q;
      if (error) throw error;
      return { data: data || [], total: count || 0 };
    }

    // Fetch in chunks to bypass the 1000-row PostgREST limit
    const globalStart = page * effectivePageSize;
    const allData: any[] = [];
    let totalCount = 0;
    let offset = globalStart;
    const globalEnd = globalStart + effectivePageSize;

    while (offset < globalEnd) {
      const batchEnd = Math.min(offset + SUPABASE_MAX - 1, globalEnd - 1);
      let q = supabase
        .from('incoming_business_metrics')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, batchEnd);
      q = applyFilters(q);
      const { data, error, count } = await q;
      if (error) throw error;
      if (count !== null) totalCount = count;
      if (!data || data.length === 0) break;
      allData.push(...data);
      offset += SUPABASE_MAX;
      if (data.length < SUPABASE_MAX) break; // no more rows
    }

    return { data: allData, total: totalCount };
  };

  const enabled = hasBrands && (brandIds.length > 1 || !!brandIds[0]);
  const query = useQuery({
    queryKey: ['business-metrics', brandIds.slice().sort().join(','), page, effectivePageSize, filters?.dateFrom ?? '', filters?.dateTo ?? ''],
    queryFn: fetchMetrics,
    enabled,
  });

  return {
    data: query.data?.data || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    query
  };
};

export const useBusinessMetricsMutations = (brandId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createMetricMutation = useMutation({
    mutationFn: async (metricData: any) => {
      const { data, error } = await supabase
        .from('incoming_business_metrics')
        .insert([metricData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-metrics', brandId] });
      toast({
        title: "Success",
        description: "Business metric created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating business metric:', error);
      toast({
        title: "Error",
        description: getFriendlyErrorMessage(error, "Failed to create business metric"),
        variant: "destructive"
      });
    }
  });

  const updateMetricMutation = useMutation({
    mutationFn: async ({ id, ...metricData }: any) => {
      const { data, error } = await supabase
        .from('incoming_business_metrics')
        .update(metricData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-metrics', brandId] });
      toast({
        title: "Success",
        description: "Business metric updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating business metric:', error);
      toast({
        title: "Error",
        description: getFriendlyErrorMessage(error, "Failed to update business metric"),
        variant: "destructive"
      });
    }
  });

  const deleteMetricMutation = useMutation({
    mutationFn: async (metricId: string) => {
      const { error } = await supabase
        .from('incoming_business_metrics')
        .delete()
        .eq('id', metricId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-metrics', brandId] });
      toast({
        title: "Success",
        description: "Business metric deleted successfully",
      });
    },
    onError: (error: any, variables: string) => {
      console.error('Error deleting business metric:', error);
      toast({
        title: "Error",
        description: getFriendlyErrorMessage(error, "Failed to delete business metric", variables),
        variant: "destructive"
      });
    }
  });

  const clearAllMetricsMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('incoming_business_metrics')
        .delete()
        .eq('brand_id', brandId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-metrics', brandId] });
      toast({
        title: "Success",
        description: "All business metrics have been deleted",
      });
    },
    onError: (error: any) => {
      console.error('Error clearing all business metrics:', error);
      toast({
        title: "Error",
        description: getFriendlyErrorMessage(error, "Failed to clear all business metrics"),
        variant: "destructive"
      });
    }
  });

  return {
    createMetric: createMetricMutation,
    updateMetric: updateMetricMutation,
    deleteMetric: deleteMetricMutation,
    clearAllMetrics: clearAllMetricsMutation
  };
};